import React, { useEffect, useState } from 'react'
import ColourPicker from '../../../../../shared/ColourPicker'
import { hapticTick } from '../../../../../../_utils/haptics'
import { COLOUR_ROLES, colourwaysFor, hardToRead, fixColours, paletteFromImage, fromBase } from '../../render/colourways'
import { loadImage } from '../../render/images'
import { ChevronLeft, ChevronRight } from '../icons'

const Swatches = ({ title, colours, value, onPick }) => (colours.length ? (
  <div className="sx-swatch-group">
    <span className="sx-swatch-title">{title}</span>
    <div className="sx-swatches">
      {colours.map(hex => (
        <button
          key={hex}
          type="button"
          className={`sx-swatch${String(value).toUpperCase() === hex.toUpperCase() ? ' is-on' : ''}`}
          style={{ background: hex }}
          onClick={() => { hapticTick(); onPick(hex) }}
          aria-label={`Use ${hex}`}
          title={hex}
        />
      ))}
    </div>
  </div>
) : null)

// A colourway as a little four-colour dot
const WayDot = ({ colours }) => (
  <span className="sx-way-dot" aria-hidden>
    {colours.map((c, i) => <i key={i} style={{ background: c }} />)}
  </span>
)

// Fine-tuning the colours, like the gift card designer: each colour the
// design uses, by what it does, each one changeable
const ColoursPanel = ({ post, theme, kit, onTune, onColourway, leadSrc }) => {
  const [editing, setEditing] = useState(null)
  const [lastEdited, setLastEdited] = useState(null)
  const [photoColours, setPhotoColours] = useState([])
  const roles = COLOUR_ROLES[post.family] || COLOUR_ROLES.editorial
  const c = theme.c
  const own = (post.tune && post.tune.colours) || {}
  const custom = Object.keys(own).length > 0
  const ways = colourwaysFor(post.family, { brandColour: kit.accent, photoColour: post.photoColour })
  const issues = hardToRead(post.family, c)

  useEffect(() => {
    let alive = true
    if (!leadSrc) { setPhotoColours([]); return undefined }
    loadImage(leadSrc, { small: true }).then((img) => { if (alive) setPhotoColours(paletteFromImage(img, 6)) })
    return () => { alive = false }
  }, [leadSrc])

  const setColour = (key, hex) => {
    setLastEdited(key)
    onTune((t) => ({ colours: { ...((t && t.colours) || {}), [key]: hex } }), `colour-${key}`)
  }
  const brandSet = kit.accent ? Array.from(new Set([kit.accent, ...fromBase(post.family, kit.accent)].map(h => h.toUpperCase()))) : []
  const designSet = Array.from(new Set([c.paper, c.ink, c.accent, c.pop, c.on].filter(Boolean).map(h => h.toUpperCase())))

  if (editing) {
    const role = roles.find(r => r.key === editing)
    return (
      <div className="sx-colour-edit">
        <button type="button" className="sx-back" onClick={() => setEditing(null)}><ChevronLeft size={16} /><span>{role ? role.name : 'Colour'}</span></button>
        <Swatches title="Your brand" colours={brandSet} value={c[editing]} onPick={(h) => setColour(editing, h)} />
        <Swatches title="From your photo" colours={photoColours} value={c[editing]} onPick={(h) => setColour(editing, h)} />
        <Swatches title="In this design" colours={designSet} value={c[editing]} onPick={(h) => setColour(editing, h)} />
        <div className="sx-picker-wrap">
          <ColourPicker value={c[editing]} onChange={(h) => setColour(editing, h)} inline paletteAa={false} />
        </div>
      </div>
    )
  }

  return (
    <div className="sx-colours">
      <section className="sx-group">
        <h3 className="sx-group-title">Start from</h3>
        <div className="sx-ways">
          {ways.map(cw => {
            const on = cw.id === post.colourway && !custom
            return (
              <button key={cw.id} type="button" className={`sx-way${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => { hapticTick(); onColourway(cw.id) }} title={cw.name}>
                <WayDot colours={cw.colours.slice(0, 4)} />
                <span>{cw.name}</span>
              </button>
            )
          })}
        </div>
      </section>
      <section className="sx-group">
        <div className="sx-group-head">
          <h3 className="sx-group-title">This design’s colours</h3>
          {custom && <button type="button" className="sx-link" onClick={() => { hapticTick(); onTune({ colours: undefined }, 'colour-reset') }}>Reset</button>}
        </div>
        <div className="sx-colour-rows">
          {roles.map(r => (
            <button key={r.key} type="button" className="sx-colour-row" onClick={() => { hapticTick(); setEditing(r.key) }}>
              <span className="sx-colour-chip" style={{ background: c[r.key] }} />
              <span className="sx-colour-name">{r.name}</span>
              <span className="sx-colour-hex">{String(c[r.key]).toUpperCase()}</span>
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
        {issues.length > 0 && (
          <div className="sx-notice is-warn" role="status">
            <span>Some words are hard to read on this colour.</span>
            <button
              type="button"
              className="sx-btn"
              onClick={() => {
                hapticTick()
                // Keep the colour they just chose; move the others to suit it
                const keep = lastEdited || roles[0].key
                onTune({ colours: { ...own, ...fixColours(post.family, c, keep) } }, 'colour-fix')
              }}
            >
              Fix it
            </button>
          </div>
        )}
      </section>
    </div>
  )
}

export default ColoursPanel
